import React, { useState } from 'react';
import {
    Modal,
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    ScrollView,
    Image,
    ActivityIndicator,
} from 'react-native';

export interface StepContext {
    stepIndex?: number;
    totalSteps?: number;
    label?: string;
    promptText?: string;
}

export interface FeedbackSubmissionPayload {
    teacherCode: string;
    activityId: string;
    kitId?: string;
    classId?: string;
    stepContext: StepContext;
    screenshotBase64?: string;
    category: string;
    feedbackText: string;
    suggestedAction?: string;
}

export interface TeacherActivityFeedbackModalProps {
    visible: boolean;
    activity?: {
        id: string;
        title?: string;
        type?: string;
        kitId?: string;
    } | null;
    classId?: string;
    stepContext?: StepContext;
    screenshotUri?: string | null;
    onSubmit: (payload: FeedbackSubmissionPayload) => Promise<void>;
    onClose: () => void;
}

const CATEGORIES = [
    'Curriculum Clarity',
    'Difficulty Spurt',
    'Typo / Error',
    'Instruction Ambiguity',
    'Visual Ambiguity',
    'Other',
];

export const TeacherActivityFeedbackModal: React.FC<TeacherActivityFeedbackModalProps> = ({
    visible,
    activity,
    classId,
    stepContext = {},
    screenshotUri,
    onSubmit,
    onClose,
}) => {
    const [teacherCode, setTeacherCode] = useState('');
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [feedbackText, setFeedbackText] = useState('');
    const [suggestedAction, setSuggestedAction] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

    if (!visible) return null;

    const stepLabel = (() => {
        if (stepContext.label && stepContext.stepIndex !== undefined && stepContext.totalSteps) {
            return `${stepContext.label} (${stepContext.stepIndex + 1} of ${stepContext.totalSteps})`;
        }
        if (stepContext.stepIndex !== undefined && stepContext.totalSteps) {
            return `Step ${stepContext.stepIndex + 1} of ${stepContext.totalSteps}`;
        }
        if (stepContext.label) return stepContext.label;
        return 'General Activity View';
    })();

    const handleScroll = (event: any) => {
        const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
        if (layoutMeasurement && contentOffset && contentSize) {
            const isNearBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 40;
            setHasScrolledToBottom(isNearBottom);
        }
    };

    const handleSubmit = async () => {
        const cleanCode = teacherCode.trim();
        if (!cleanCode || cleanCode.length < 6) {
            setErrorMessage('Please enter your 6-digit teacher code');
            return;
        }

        if (!feedbackText.trim()) {
            setErrorMessage('Please describe your feedback or observation');
            return;
        }

        if (!activity?.id) {
            setErrorMessage('No active activity selected');
            return;
        }

        setErrorMessage(null);
        setIsSubmitting(true);

        try {
            await onSubmit({
                teacherCode: cleanCode,
                activityId: activity.id,
                kitId: activity.kitId,
                classId,
                stepContext,
                screenshotBase64: screenshotUri || undefined,
                category,
                feedbackText: feedbackText.trim(),
                suggestedAction: suggestedAction.trim() || undefined,
            });

            // Reset state upon successful submission
            setFeedbackText('');
            setSuggestedAction('');
            setTeacherCode('');
            onClose();
        } catch (err: any) {
            setErrorMessage(err.message || 'Failed to submit feedback. Please check your teacher code.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
            <View style={styles.overlay}>
                <View style={styles.cardContainer} accessibilityRole="dialog" accessibilityLabel="Teacher Feedback Dialog">
                    <View style={styles.headerRow}>
                        <View style={styles.badgePill}>
                            <Text style={styles.badgeText}>👩‍🏫 Teacher Review</Text>
                        </View>
                        <TouchableOpacity
                            onPress={onClose}
                            accessibilityLabel="Close feedback dialog"
                            style={styles.closeButton}
                        >
                            <Text style={styles.closeText}>✕</Text>
                        </TouchableOpacity>
                    </View>

                    <Text style={styles.title}>Teacher Activity Feedback</Text>
                    <Text style={styles.subtitle}>
                        Flag improvements or errors directly to school curriculum leads and the EduWrite marketplace.
                    </Text>

                    {/* Scroll hint bar */}
                    <View style={styles.scrollIndicatorBar}>
                        <Text style={styles.scrollIndicatorText}>↕ Scrollable Form — complete all details below</Text>
                    </View>

                    <ScrollView
                        style={styles.scrollArea}
                        contentContainerStyle={styles.scrollContent}
                        showsVerticalScrollIndicator={true}
                        persistentScrollbar={true}
                        onScroll={handleScroll}
                        scrollEventThrottle={16}
                    >
                        {/* Context preview bar */}
                        <View style={styles.contextBox}>
                            <Text style={styles.activityTitle}>{activity?.title || 'Current Activity'}</Text>
                            <Text style={styles.stepBadge}>{stepLabel}</Text>
                        </View>

                        {/* Screenshot thumbnail if available */}
                        {screenshotUri ? (
                            <View style={styles.screenshotBox}>
                                <Text style={styles.fieldLabel}>Screen Snapshot Captured:</Text>
                                <Image
                                    source={{ uri: screenshotUri }}
                                    style={styles.screenshotImage}
                                    resizeMode="cover"
                                    accessibilityLabel="Snapshot of the activity screen"
                                />
                            </View>
                        ) : null}

                        {/* Category selection */}
                        <Text style={styles.fieldLabel}>Feedback Category:</Text>
                        <View style={styles.chipsContainer}>
                            {CATEGORIES.map((cat) => {
                                const isSelected = category === cat;
                                return (
                                    <TouchableOpacity
                                        key={cat}
                                        onPress={() => setCategory(cat)}
                                        style={[styles.chip, isSelected && styles.chipSelected]}
                                        accessibilityRole="button"
                                        accessibilityState={{ selected: isSelected }}
                                    >
                                        <Text style={[styles.chipText, isSelected && styles.chipTextSelected]}>
                                            {cat}
                                        </Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>

                        {/* Error banner */}
                        {errorMessage ? (
                            <View style={styles.errorBox}>
                                <Text style={styles.errorText}>{errorMessage}</Text>
                            </View>
                        ) : null}

                        {/* Teacher Code (Anti-abuse & Auth with source guidance) */}
                        <View style={styles.teacherCodeSection}>
                            <View style={styles.fieldHeaderRow}>
                                <Text style={styles.fieldLabel}>Teacher Verification Code (6 digits):</Text>
                                <View style={styles.requiredPill}>
                                    <Text style={styles.requiredPillText}>Required</Text>
                                </View>
                            </View>

                            <TextInput
                                style={styles.inputCode}
                                placeholder="6-digit Teacher Code"
                                placeholderTextColor="#94A3B8"
                                value={teacherCode}
                                onChangeText={(text) => {
                                    setTeacherCode(text);
                                    if (errorMessage) setErrorMessage(null);
                                }}
                                keyboardType="numeric"
                                maxLength={6}
                                accessibilityLabel="6-digit Teacher Code"
                            />

                            {/* Clear guidance on where to get the teacher code */}
                            <View style={styles.codeSourceCallout}>
                                <Text style={styles.codeSourceTitle}>🔑 Where to get your Teacher Code:</Text>
                                <Text style={styles.codeSourceBody}>
                                    1. Open the <Text style={styles.codeSourceBold}>Teacher Portal</Text> (on your laptop, tablet, or phone).{'\n'}
                                    2. Go to the <Text style={styles.codeSourceBold}>Classrooms</Text> dashboard.{'\n'}
                                    3. Click the <Text style={styles.codeSourceBold}>"Device Code"</Text> button in the top toolbar to view or generate your active 6-digit code.{'\n'}
                                    <Text style={styles.codeSourceMuted}>In Demo Mode: Open the Teacher Portal dashboard and click "Device Code" to copy your active code.</Text>
                                </Text>
                            </View>
                        </View>

                        {/* Teacher feedback comments */}
                        <Text style={styles.fieldLabel}>Feedback & Observations:</Text>
                        <TextInput
                            style={styles.inputTextarea}
                            placeholder="Describe the issue or improvement..."
                            placeholderTextColor="#94A3B8"
                            value={feedbackText}
                            onChangeText={(text) => {
                                setFeedbackText(text);
                                if (errorMessage) setErrorMessage(null);
                            }}
                            multiline
                            numberOfLines={4}
                            textAlignVertical="top"
                            accessibilityLabel="Feedback text"
                        />

                        {/* Optional suggested replacement */}
                        <Text style={styles.fieldLabel}>Suggested Alternative / Fix (Optional):</Text>
                        <TextInput
                            style={styles.inputField}
                            placeholder="e.g. Rephrase prompt to..."
                            placeholderTextColor="#94A3B8"
                            value={suggestedAction}
                            onChangeText={setSuggestedAction}
                            accessibilityLabel="Suggested alternative"
                        />
                    </ScrollView>

                    {/* Floating scroll cue when not at bottom */}
                    {!hasScrolledToBottom && (
                        <View style={styles.moreBelowCue}>
                            <Text style={styles.moreBelowText}>↓ More fields below — scroll down to complete & submit ↓</Text>
                        </View>
                    )}

                    {/* Action buttons */}
                    <View style={styles.actionsRow}>
                        <TouchableOpacity
                            onPress={onClose}
                            style={styles.cancelBtn}
                            accessibilityRole="button"
                            accessibilityLabel="Cancel feedback"
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            testID="submit-feedback-btn"
                            onPress={handleSubmit}
                            disabled={isSubmitting}
                            style={[styles.submitBtn, isSubmitting && styles.submitBtnDisabled]}
                            accessibilityRole="button"
                            accessibilityLabel="Submit Feedback"
                        >
                            {isSubmitting ? (
                                <ActivityIndicator color="#FFFFFF" />
                            ) : (
                                <Text style={styles.submitBtnText}>Submit Feedback</Text>
                            )}
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 16,
    },
    cardContainer: {
        width: '100%',
        maxWidth: 580,
        maxHeight: '90%',
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 24,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.25,
        shadowRadius: 20,
        elevation: 8,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    badgePill: {
        backgroundColor: '#EEF2FF',
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 9999,
    },
    badgeText: {
        color: '#4F46E5',
        fontWeight: '700',
        fontSize: 13,
    },
    closeButton: {
        minWidth: 44,
        minHeight: 44,
        justifyContent: 'center',
        alignItems: 'center',
    },
    closeText: {
        fontSize: 20,
        color: '#64748B',
        fontWeight: 'bold',
    },
    title: {
        fontSize: 22,
        fontWeight: '800',
        color: '#1E293B',
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 13,
        color: '#64748B',
        marginBottom: 16,
        lineHeight: 18,
    },
    scrollIndicatorBar: {
        backgroundColor: '#F1F5F9',
        borderRadius: 8,
        paddingVertical: 5,
        paddingHorizontal: 10,
        marginBottom: 8,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scrollIndicatorText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#64748B',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    scrollArea: {
        maxHeight: 420,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#F1F5F9',
    },
    scrollContent: {
        paddingVertical: 10,
        paddingHorizontal: 2,
    },
    moreBelowCue: {
        backgroundColor: '#EFF6FF',
        borderWidth: 1,
        borderColor: '#BFDBFE',
        borderRadius: 8,
        paddingVertical: 6,
        paddingHorizontal: 12,
        marginTop: 6,
        alignItems: 'center',
    },
    moreBelowText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#2563EB',
    },
    contextBox: {
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 12,
        padding: 12,
        marginBottom: 16,
    },
    activityTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#0F172A',
    },
    stepBadge: {
        fontSize: 13,
        color: '#6366F1',
        fontWeight: '600',
        marginTop: 2,
    },
    screenshotBox: {
        marginBottom: 16,
    },
    screenshotImage: {
        width: '100%',
        height: 120,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        marginTop: 6,
    },
    fieldLabel: {
        fontSize: 13,
        fontWeight: '700',
        color: '#334155',
        marginBottom: 6,
    },
    teacherCodeSection: {
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 14,
        padding: 14,
        marginBottom: 16,
    },
    fieldHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    requiredPill: {
        backgroundColor: '#FEF3C7',
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
    requiredPillText: {
        color: '#D97706',
        fontSize: 10,
        fontWeight: '800',
        textTransform: 'uppercase',
    },
    codeSourceCallout: {
        backgroundColor: '#EEF2FF',
        borderLeftWidth: 4,
        borderLeftColor: '#6366F1',
        borderRadius: 8,
        padding: 10,
        marginTop: 8,
    },
    codeSourceTitle: {
        fontSize: 12,
        fontWeight: '800',
        color: '#3730A3',
        marginBottom: 4,
    },
    codeSourceBody: {
        fontSize: 12,
        color: '#4338CA',
        lineHeight: 18,
    },
    codeSourceBold: {
        fontWeight: '800',
        color: '#312E81',
    },
    codeSourceMuted: {
        fontSize: 11,
        color: '#6366F1',
        fontStyle: 'italic',
        marginTop: 4,
    },
    chipsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 16,
    },
    chip: {
        backgroundColor: '#F1F5F9',
        borderRadius: 20,
        paddingHorizontal: 12,
        paddingVertical: 8,
        minHeight: 40,
        justifyContent: 'center',
    },
    chipSelected: {
        backgroundColor: '#4F46E5',
    },
    chipText: {
        fontSize: 12,
        fontWeight: '600',
        color: '#475569',
    },
    chipTextSelected: {
        color: '#FFFFFF',
    },
    errorBox: {
        backgroundColor: '#FEF2F2',
        borderWidth: 1,
        borderColor: '#FCA5A5',
        borderRadius: 8,
        padding: 10,
        marginBottom: 12,
    },
    errorText: {
        color: '#DC2626',
        fontSize: 13,
        fontWeight: '600',
    },
    inputCode: {
        backgroundColor: '#F8FAFC',
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        borderRadius: 12,
        paddingHorizontal: 16,
        height: 52,
        fontSize: 18,
        fontWeight: '700',
        color: '#0F172A',
        letterSpacing: 3,
        marginBottom: 16,
    },
    inputTextarea: {
        backgroundColor: '#F8FAFC',
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        borderRadius: 12,
        padding: 12,
        height: 90,
        fontSize: 14,
        color: '#0F172A',
        marginBottom: 16,
    },
    inputField: {
        backgroundColor: '#F8FAFC',
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        borderRadius: 12,
        paddingHorizontal: 14,
        height: 48,
        fontSize: 14,
        color: '#0F172A',
        marginBottom: 16,
    },
    actionsRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
        marginTop: 16,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    cancelBtn: {
        minHeight: 56,
        minWidth: 100,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 16,
        borderRadius: 14,
        backgroundColor: '#F1F5F9',
    },
    cancelBtnText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#64748B',
    },
    submitBtn: {
        minHeight: 56,
        minWidth: 160,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 20,
        borderRadius: 14,
        backgroundColor: '#22C55E', // Vibrant apple green per student-ui-craft
        borderBottomWidth: 4,
        borderBottomColor: '#16A34A',
    },
    submitBtnDisabled: {
        backgroundColor: '#94A3B8',
        borderBottomColor: '#64748B',
    },
    submitBtnText: {
        fontSize: 16,
        fontWeight: '800',
        color: '#FFFFFF',
    },
});
